import { 
    Box, 
    Button, 
    Checkbox, 
    CircularProgress, 
    Dialog, 
    DialogActions, 
    DialogContent, 
    DialogTitle, 
    FormControlLabel, 
    Stack, 
    Switch, 
    TextField,
    Typography,
    Divider,
    Paper,
    Chip,
    IconButton
} from "@mui/material";
import { Timer, SmartToy, Close } from "@mui/icons-material";
import { Assessment, newAssessment } from "../../../../models/assessment";
import { useState } from "react";
import StarIcon from '../assets/icons/star.png';
import { useClassroomActions } from "../../../../hooks/useClassroomActions";

interface NewAssessmentDialogProps {
    open: boolean;
    setOpen: React.Dispatch<React.SetStateAction<boolean>>;
    classroom_id: string;
    setRunningTask: React.Dispatch<React.SetStateAction<number>>;
}

export default function NewAssessmentDialog({ open, setOpen, classroom_id, setRunningTask }: NewAssessmentDialogProps) {
    const { createAssessment } = useClassroomActions();
    
    const [assessment, setAssessment] = useState<Assessment>(newAssessment());
    const [isSubmitting, setIsSubmitting] = useState(false);

    const isFormValid = assessment.title && (!assessment.timer || assessment.duration > 0);

    const handleAssessmentChange = (field: keyof Assessment, value: any) => {
        setAssessment((prev) => ({
            ...prev,
            [field]: value
        }));
    }

    const handleSubmit = async () => {
        setRunningTask(assessment.id);
        setOpen(false);
        await createAssessment(Number(classroom_id), assessment);
        setRunningTask(-1);
        setAssessment(newAssessment());
    }

    return (
        <Dialog 
            open={open} 
            onClose={() => setOpen(false)} 
            fullWidth 
            maxWidth="sm"
            PaperProps={{
                sx: {
                    borderRadius: 3,
                    boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)"
                }
            }}
        >
            <DialogTitle 
                sx={{ 
                    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                    color: 'white',
                    padding: '20px 24px',
                    position: 'relative',
                    '&::after': {
                        content: '""',
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        height: '3px',
                        background: 'linear-gradient(90deg, #10b981, #059669, #10b981)',
                        backgroundSize: '200% 100%',
                        animation: 'shimmer 2s infinite',
                    },
                    '@keyframes shimmer': {
                        '0%': { backgroundPosition: '-200% 0' },
                        '100%': { backgroundPosition: '200% 0' },
                    }
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box
                        sx={{
                            width: 44,
                            height: 44,
                            borderRadius: '12px',
                            backgroundColor: 'rgba(255,255,255,0.2)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backdropFilter: 'blur(10px)',
                        }}
                    >
                        ✍️
                    </Box>
                    <Box>
                        <Typography variant="h6" fontWeight="600" sx={{ mb: 0.5 }}>
                            Create New Assessment
                        </Typography>
                        <Typography variant="body2" sx={{ opacity: 0.9, fontSize: '0.875rem' }}>
                            Assess your students knowledge in a specific field
                        </Typography>
                    </Box>
                </Box>

                <IconButton
                    onClick={() => setOpen(false)}
                    disabled={isSubmitting}
                    sx={{
                        position: 'absolute',
                        right: 16,
                        top: 16,
                        color: 'white',
                        backgroundColor: 'rgba(255,255,255,0.1)',
                        backdropFilter: 'blur(10px)',
                        '&:hover': {
                            backgroundColor: 'rgba(255,255,255,0.2)',
                            transform: 'scale(1.1)',
                        },
                        '&:disabled': {
                            color: 'rgba(255,255,255,0.5)',
                            backgroundColor: 'rgba(255,255,255,0.05)',
                        },
                        transition: 'all 0.2s ease',
                    }}
                >
                    <Close />
                </IconButton>
            </DialogTitle>
            
            <DialogContent>
                <Stack spacing={2} pt={2}>
                    <TextField
                        label="Assessment Title"
                        value={assessment.title}
                        onChange={(e) => handleAssessmentChange("title", e.target.value)}
                        fullWidth
                        sx={{
                        '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                            backgroundColor: '#f8f9fa',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                                backgroundColor: '#f0f1f3',
                            },
                            '&.Mui-focused': {
                                backgroundColor: '#ffffff',
                                boxShadow: '0 0 0 3px rgba(83, 130, 107, 0.1)',
                            },
                        },
                        '& .MuiInputLabel-root.Mui-focused': {
                            color: '#53826b',
                        },
                        '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#53826b',
                            borderWidth: 2,
                        },
                        }}
                    />

                    <Paper 
                        elevation={0} 
                        sx={{ 
                            p: 2, 
                            backgroundColor: assessment.timer ? '#f0f9ff' : '#f8f9fa',
                            border: `1px solid ${assessment.timer ? '#e0f2fe' : '#e2e8f0'}`,
                            borderRadius: 2,
                            transition: 'all 0.3s ease'
                        }}
                    >
                        <Stack spacing={2}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Timer 
                                    sx={{ 
                                        color: assessment.timer ? '#0284c7' : '#64748b',
                                        fontSize: 20 
                                    }} 
                                />
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={assessment.timer}
                                            onChange={(e) => {
                                                handleAssessmentChange("timer", e.target.checked);
                                                if (!e.target.checked) {
                                                    handleAssessmentChange("duration", 0);
                                                } else {
                                                    handleAssessmentChange("duration", 60);
                                                }
                                            }}
                                            sx={{
                                                '& .MuiSwitch-switchBase.Mui-checked': {
                                                    color: '#0284c7',
                                                },
                                                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                                    backgroundColor: '#0284c7',
                                                },
                                            }}
                                        />
                                    }
                                    label={
                                        <Box>
                                            <Typography variant="body1" fontWeight={500}>
                                                Timer
                                            </Typography>
                                        </Box>
                                    }
                                />
                    
                            </Box>

                            {assessment.timer && (
                                <Box sx={{ ml: 4, pt: 1 }}>
                                    <TextField
                                        type="number"
                                        label="Duration (minutes)"
                                        value={assessment.duration}
                                        onChange={(e) => handleAssessmentChange("duration", e.target.value)}
                                        size="small"
                                        sx={{ 
                                            width: 200,
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: 1.5,
                                            },
                                        }}
                                    />
                                </Box>
                            )}
                        </Stack>
                    </Paper>

                    <Paper 
                        elevation={0} 
                        sx={{ 
                            p: 2, 
                            backgroundColor: assessment.auto_marker ? '#f0fdf4' : '#f8f9fa',
                            border: `1px solid ${assessment.auto_marker ? '#dcfce7' : '#e2e8f0'}`,
                            borderRadius: 2,
                            transition: 'all 0.3s ease'
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <img 
                                src={StarIcon} 
                                alt="AI" 
                                style={{ 
                                    width: 24, 
                                    height: 24,
                                }} 
                            /> 
                            
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={assessment.auto_marker}
                                        onChange={(e) => handleAssessmentChange("auto_marker", e.target.checked)}
                                        sx={{
                                            color: '#64748b',
                                            '&.Mui-checked': {
                                                color: '#10b981',
                                            },
                                        }}
                                    />
                                }
                                label={
                                    <Box>
                                        <Typography variant="body1" fontWeight={500}>
                                            Auto-Grading
                                        </Typography>
                                    </Box>
                                }
                            />
                        </Box>
                    </Paper>
                </Stack>
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 3, pt: 1, justifyContent: 'flex-end' }}>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    disabled={!isFormValid || isSubmitting}
                    sx={{ 
                        backgroundColor: "#10b981",
                        borderRadius: 2,
                        textTransform: 'none',
                        fontWeight: 500,
                        px: 4,
                        py: 1,
                        minWidth: 160,
                        '&:hover': {
                            backgroundColor: "#059669"
                        },
                        '&:disabled': {
                            backgroundColor: '#e5e7eb'
                        }
                    }}
                >
                    {isSubmitting ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <CircularProgress size={18} color="inherit" />
                            Creating...
                        </Box>
                    ) : (
                        "Create Assessment"
                    )}
                </Button>
            </DialogActions>
        </Dialog>
    );
}